import { Calendar, Eye, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'

import { getSalesTargetsAndAchievements } from '../../api/sales.api'

const MONTH_NAMES = [
  { month: 1, name: 'January', short: 'Jan' },
  { month: 2, name: 'February', short: 'Feb' },
  { month: 3, name: 'March', short: 'Mar' },
  { month: 4, name: 'April', short: 'Apr' },
  { month: 5, name: 'May', short: 'May' },
  { month: 6, name: 'June', short: 'Jun' },
  { month: 7, name: 'July', short: 'Jul' },
  { month: 8, name: 'August', short: 'Aug' },
  { month: 9, name: 'September', short: 'Sep' },
  { month: 10, name: 'October', short: 'Oct' },
  { month: 11, name: 'November', short: 'Nov' },
  { month: 12, name: 'December', short: 'Dec' },
]

export function MonthlyTargetsModal({ open, onOpenChange, sales }) {
  const currentMonth = new Date().getMonth() + 1
  const currentYear = new Date().getFullYear()
  const year = currentYear
  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!open || !sales?._id) return

    const loadTargets = async () => {
      try {
        setIsLoading(true)
        const res = await getSalesTargetsAndAchievements(sales._id, year)
        setData(res?.data || null)
      } catch (err) {
        console.error('Failed to load targets and achievements:', err)
        // Fallback to salesperson document targets if available
        const targetsMap = {}
        ;(sales.monthlyTargets || []).forEach((t) => {
          if (Number(t.year) === year) {
            targetsMap[Number(t.month)] = Number(t.target)
          }
        })
        const months = MONTH_NAMES.map((m) => ({
          month: m.month,
          name: m.name,
          short: m.short,
          year,
          target: targetsMap[m.month] ?? null,
          achieved: 0,
          percentage: 0,
        }))
        setData({
          sales: {
            _id: sales._id,
            name: sales.name,
            email: sales.email,
          },
          year,
          totalTarget: Object.values(targetsMap).reduce((a, b) => a + b, 0),
          totalAchieved: 0,
          overallPercentage: 0,
          months,
        })
      } finally {
        setIsLoading(false)
      }
    }

    loadTargets()
  }, [open, sales, year])

  const months = data?.months || []
  const totalTarget = data?.totalTarget || 0
  const totalAchieved = data?.totalAchieved || 0
  const overallPercentage = data?.overallPercentage || 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl sm:rounded-3xl p-4 sm:p-7 shadow-2xl">
        <DialogHeader className="space-y-1">
          <div className="flex items-center gap-2 text-indigo-600">
            <Eye className="h-5 w-5" />
            <span className="text-xs font-bold uppercase tracking-wider">
              Performance & Target Overview
            </span>
          </div>
          <DialogTitle className="text-lg sm:text-xl font-bold tracking-tight text-slate-900">
            Monthly Targets & Achievements
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Track all monthly assigned targets and closed orders for{' '}
            <span className="font-semibold text-slate-700">{sales?.name}</span>{' '}
            ({year}).
          </DialogDescription>
        </DialogHeader>

        {/* Top KPI Cards */}
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
          <div className="rounded-2xl border border-slate-100 bg-slate-50/80 p-3 text-center">
            <p className="text-[11px] font-medium text-slate-500">
              Total Target
            </p>
            <p className="mt-1 text-xl sm:text-2xl font-black text-slate-900">
              ₹{totalTarget.toLocaleString('en-IN')}
            </p>
            <p className="mt-0.5 text-[10px] text-slate-400">
              Assigned across {year}
            </p>
          </div>

          <div className="rounded-2xl border border-emerald-100 bg-emerald-50/50 p-3 text-center">
            <p className="text-[11px] font-medium text-emerald-700">
              Total Achieved
            </p>
            <p className="mt-1 text-xl sm:text-2xl font-black text-emerald-600">
              ₹{totalAchieved.toLocaleString('en-IN')}
            </p>
            <p className="mt-0.5 text-[10px] text-emerald-600/70">
              Revenue achieved
            </p>
          </div>

          <div className="rounded-2xl border border-indigo-100 bg-indigo-50/50 p-3 text-center">
            <p className="text-[11px] font-medium text-indigo-700">
              Overall Rate
            </p>
            <p className="mt-1 text-xl sm:text-2xl font-black text-indigo-600">
              {overallPercentage}%
            </p>
            <p className="mt-0.5 text-[10px] text-indigo-600/70">
              Achievement rate
            </p>
          </div>
        </div>

        {/* 12 Months Grid */}
        <div className="mt-2">
          <div className="flex items-center justify-between px-1 py-1">
            <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
              {year} Monthly Breakdown
            </span>
            <span className="text-[11px] text-slate-400">12 Months</span>
          </div>

          {isLoading ? (
            <div className="flex h-64 flex-col items-center justify-center gap-2 rounded-2xl border border-slate-100 bg-slate-50">
              <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
              <span className="text-xs text-slate-500">
                Loading performance data...
              </span>
            </div>
          ) : (
            <div className="max-h-[380px] overflow-y-auto rounded-2xl border border-slate-100 p-1.5 space-y-1.5 bg-slate-50/40">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {months.map((m) => {
                  const isCurrent =
                    m.month === currentMonth && m.year === currentYear
                  const targetNum = m.target != null ? Number(m.target) : null
                  const achievedNum = Number(m.achieved || 0)
                  const isMet =
                    targetNum != null &&
                    targetNum > 0 &&
                    achievedNum >= targetNum
                  const pct =
                    m.percentage ??
                    (targetNum && targetNum > 0
                      ? Math.round((achievedNum / targetNum) * 100)
                      : 0)

                  return (
                    <div
                      key={m.month}
                      className={`relative flex flex-col justify-between rounded-2xl border p-3 transition-all ${
                        isCurrent
                          ? 'border-indigo-300 bg-indigo-50/40 shadow-xs ring-1 ring-indigo-200'
                          : 'border-slate-100 bg-white hover:border-slate-200 shadow-2xs'
                      }`}
                    >
                      {/* Top row: Month Name + Badge */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-xs font-bold text-slate-800">
                            {m.name}
                          </span>
                        </div>

                        {isCurrent ? (
                          <span className="rounded-full bg-indigo-600 px-2 py-0.5 text-[9px] font-bold text-white uppercase tracking-wider">
                            Current
                          </span>
                        ) : isMet ? (
                          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[9px] font-bold text-emerald-700 uppercase tracking-wider">
                            Achieved
                          </span>
                        ) : targetNum != null ? (
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[9px] font-semibold text-slate-600">
                            {pct}%
                          </span>
                        ) : (
                          <span className="text-[10px] text-slate-400">
                            Not Set
                          </span>
                        )}
                      </div>

                      {/* Middle row: Assigned vs Achieved */}
                      <div className="mt-2.5 grid grid-cols-2 gap-2 rounded-xl bg-slate-50/70 p-2 text-center border border-slate-100/60">
                        <div>
                          <p className="text-[10px] text-slate-400 font-medium">
                            Assigned
                          </p>
                          <p className="text-sm font-bold text-slate-800">
                            {targetNum != null
                              ? `₹${targetNum.toLocaleString('en-IN')}`
                              : '—'}
                          </p>
                        </div>
                        <div className="border-l border-slate-200">
                          <p className="text-[10px] text-slate-400 font-medium">
                            Achieved
                          </p>
                          <p className="text-sm font-bold text-emerald-600">
                            ₹{achievedNum.toLocaleString('en-IN')}
                          </p>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      {targetNum != null && targetNum > 0 && (
                        <div className="mt-2 space-y-1">
                          <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                            <div
                              className={`h-full rounded-full transition-all duration-300 ${
                                isMet ? 'bg-emerald-500' : 'bg-indigo-500'
                              }`}
                              style={{ width: `${Math.min(100, pct)}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-3 flex justify-end">
          <Button
            type="button"
            variant="outline"
            className="rounded-xl px-5 text-xs font-semibold"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
